import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageSearch } from 'lucide-react';
import { Popover, type PopoverState } from 'react-tiny-popover';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput';
import { canEnterBundleQuantity } from '~/components/ui/bundleQuantity';
import { popoverMaxHeight } from '~/components/ui/popoverMaxHeight';
import { FieldError } from '~/components/ui/inputs/FieldError';
import type { Quantity } from '~/graphql/graphql';
import { formatLengthWithBasis } from '~/utils';
import { BundleQuantityLabel } from '~/components/ui/BundleQuantityLabel';
import type { BuyListDetailItem } from '~/pages/buy-lists/sections/types';
import {
  SelectableRow,
  SelectableRowLabel,
} from '~/pages/buy-lists/sections/SelectableRow';
import { SelectionConflictMessages } from '~/pages/buy-lists/sections/SelectionConflictMessages';
import {
  conflictsOfRow,
  NO_SELECTION_CONFLICTS,
  type SelectionConflict,
  type SelectionConflictsByRowId,
} from '~/pages/buy-lists/sections/selectionConflicts';
import {
  SelectBuyListTalliesSection,
  type SelectBuyListTalliesSectionProps,
} from '~/pages/buy-lists/sections/tallies/SelectBuyListTalliesSection';

type SelectBuyListItemsStepProps = {
  items: BuyListDetailItem[];
  selectedItemIds: Set<string>;
  onToggleItem: (itemId: string) => void;
  quantitiesByItemId: Map<string, Quantity>;
  onQuantityChange: (itemId: string, quantity: Quantity | null) => void;
  tallySelection: Omit<SelectBuyListTalliesSectionProps, 'conflictsByRowId'>;
  conflictsByRowId?: SelectionConflictsByRowId;
};

type BuyListItemRowProps = {
  item: BuyListDetailItem;
  isSelected: boolean;
  conflicts: SelectionConflict[];
  onToggleItem: (itemId: string) => void;
  quantity: Quantity;
  onQuantityChange: (itemId: string, quantity: Quantity | null) => void;
};

type ChangeQuantityPopoverProps = {
  item: BuyListDetailItem;
  quantity: Quantity;
  onQuantityChange: (itemId: string, quantity: Quantity | null) => void;
};

function ChangeQuantityPopover({
  item,
  quantity,
  onQuantityChange,
}: Readonly<ChangeQuantityPopoverProps>) {
  const { t } = useTranslation('inventory');
  const [isOpen, setIsOpen] = useState(false);

  // Unstopped, Escape reaches the modal's document listener and closes the whole wizard.
  const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.nativeEvent.stopPropagation();
    setIsOpen(false);
  };

  return (
    <Popover
      isOpen={isOpen}
      onClickOutside={() => setIsOpen(false)}
      positions={['bottom', 'top', 'left']}
      align="end"
      containerStyle={{ zIndex: '1100' }}
      content={(popoverState: PopoverState) => (
        <div
          data-testid={`rfq-quantity-popover-${item.id}`}
          className="mt-2 w-64 overflow-y-auto rounded-xl border border-neut-border bg-neut-light p-3 shadow-xl"
          style={{ maxHeight: popoverMaxHeight(popoverState) }}
          onKeyDown={closeOnEscape}
        >
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="mb-2 text-xs font-semibold text-neut-prim">
              {t('inventory.buy_list.request_for_quote.quantity_label')}
            </legend>
            <BundleQuantityInput
              quantity={quantity}
              platformProductsLength={item.platformProductsLength}
              piecesPerBundle={item.effectivePiecesPerBundle}
              onBundleChange={(nextQuantity) =>
                onQuantityChange(item.id, nextQuantity)
              }
            />
          </fieldset>
          <button
            type="button"
            data-testid={`rfq-quantity-done-${item.id}`}
            className="mt-3 w-full cursor-pointer rounded-lg bg-prim-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-prim-700"
            onClick={() => setIsOpen(false)}
          >
            {t('inventory.buy_list.request_for_quote.quantity_done')}
          </button>
        </div>
      )}
    >
      <button
        type="button"
        data-testid={`rfq-quantity-change-${item.id}`}
        aria-expanded={isOpen}
        className="shrink-0 cursor-pointer rounded-lg border border-neut-border bg-neut-light px-2.5 py-1.5 text-xs font-medium text-prim-700 hover:border-prim-300 hover:bg-prim-50"
        onClick={() => setIsOpen((wasOpen) => !wasOpen)}
      >
        {t('inventory.buy_list.request_for_quote.quantity_change')}
      </button>
    </Popover>
  );
}

function BuyListItemRow({
  item,
  isSelected,
  conflicts,
  onToggleItem,
  quantity,
  onQuantityChange,
}: Readonly<BuyListItemRowProps>) {
  const { t } = useTranslation('inventory');
  const platformProductsLength = item.platformProductsLength;
  const productName = platformProductsLength.platformProduct.name;
  const productCode = platformProductsLength.platformProduct.code;
  const lengthLabel = formatLengthWithBasis(
    platformProductsLength.lengthDisplayValue,
    platformProductsLength.isNominal
  );
  const productMetadata = productCode
    ? `${productCode} · ${lengthLabel}`
    : lengthLabel;
  const isEditable =
    isSelected &&
    canEnterBundleQuantity(
      platformProductsLength,
      quantity.unit,
      item.effectivePiecesPerBundle
    );
  const checkboxId = `rfq-item-${item.id}`;
  const conflictsId = `${checkboxId}-conflicts`;
  const hasConflicts = conflicts.length > 0;

  return (
    <SelectableRow
      testId={`rfq-item-row-${item.id}`}
      isSelected={isSelected}
      layoutClassName="flex flex-col gap-2"
    >
      <div className="flex items-center gap-4">
        <SelectableRowLabel
          checkboxId={checkboxId}
          describedById={hasConflicts ? conflictsId : undefined}
          isSelected={isSelected}
          onToggle={() => onToggleItem(item.id)}
        >
          <span className="truncate text-sm font-semibold text-neut-prim">
            {productName}
          </span>
          <span className="truncate text-xs text-neut-sec">
            {productMetadata}
          </span>
        </SelectableRowLabel>
        <div
          data-testid={`rfq-item-quantity-${item.id}`}
          className="flex shrink-0 items-center gap-3"
        >
          <span className="flex flex-col items-end gap-0.5">
            <BundleQuantityLabel
              className="text-sm font-semibold text-neut-prim"
              quantity={quantity}
              piecesPerBundle={item.effectivePiecesPerBundle}
              platformProductsLength={platformProductsLength}
              formatLeadLabel={(label) =>
                t('inventory.buy_list.request_for_quote.quantity_requested', {
                  quantity: label,
                })
              }
            />
            <BundleQuantityLabel
              className="text-xs text-neut-sec"
              quantity={item.quantity}
              piecesPerBundle={item.effectivePiecesPerBundle}
              platformProductsLength={platformProductsLength}
              formatLeadLabel={(label) =>
                t('inventory.buy_list.request_for_quote.quantity_buy_list', {
                  quantity: label,
                })
              }
            />
            {isSelected && quantity.value <= 0 && (
              <FieldError
                testId={`rfq-quantity-invalid-${item.id}`}
                tone="warning"
              >
                {t('inventory.buy_list.request_for_quote.quantity_invalid')}
              </FieldError>
            )}
          </span>
          {isEditable && (
            <ChangeQuantityPopover
              item={item}
              quantity={quantity}
              onQuantityChange={onQuantityChange}
            />
          )}
        </div>
      </div>
      {hasConflicts && (
        <SelectionConflictMessages
          id={conflictsId}
          testId={`rfq-item-conflict-${item.id}`}
          conflicts={conflicts}
        />
      )}
    </SelectableRow>
  );
}

export function SelectBuyListItemsStep({
  items,
  selectedItemIds,
  onToggleItem,
  quantitiesByItemId,
  onQuantityChange,
  tallySelection,
  conflictsByRowId = NO_SELECTION_CONFLICTS,
}: Readonly<SelectBuyListItemsStepProps>) {
  const { t } = useTranslation('inventory');

  if (items.length === 0 && tallySelection.tallies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-neut-300 bg-neut-50 px-6 py-12 text-center">
        <PackageSearch size={28} aria-hidden="true" className="text-neut-400" />
        <p className="text-sm text-neut-sec">
          {t('inventory.buy_list.request_for_quote.no_items')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="buy-list-item-selection" className="flex flex-col gap-4">
      {items.length > 0 && (
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <li key={item.id}>
              <BuyListItemRow
                item={item}
                isSelected={selectedItemIds.has(item.id)}
                conflicts={conflictsOfRow(conflictsByRowId, item.id)}
                onToggleItem={onToggleItem}
                quantity={quantitiesByItemId.get(item.id) ?? item.quantity}
                onQuantityChange={onQuantityChange}
              />
            </li>
          ))}
        </ul>
      )}
      <SelectBuyListTalliesSection
        {...tallySelection}
        conflictsByRowId={conflictsByRowId}
      />
    </div>
  );
}
